import { useCallback } from 'react';

import { canSelectDocType } from 'src/features/myWork/helpers';
import useSnackbarProvider from 'src/hooks/useSnackbarProvider';
import { IFormValues } from 'src/pages/MyWorkProofDetailPage/components/AsideProofActions';
import { useAppDispatch } from 'src/store';
import { IProof } from 'src/ts/interfaces';
import { updateProofThunk } from '../services';

export const useProofUpdateAside = () => {
  const dispatch = useAppDispatch();

  const { SnackTypes, showSnackbar, VariantType } = useSnackbarProvider();

  const onUpdateProof = useCallback(
    async (proofId?: string, formValues?: IFormValues) => {
      if (!proofId) {
        showSnackbar(VariantType.error, 'Is missing the Id to update the Proof', SnackTypes.none);

        return;
      }

      const canSaveDocType = canSelectDocType(
        formValues?.result,
        formValues?.resultInsufficientReason
      );
      const type = canSaveDocType ? { type: formValues?.document } : {};
      const result = formValues?.result ? { result: formValues?.result } : {};
      const note = formValues?.note;

      // resultInsufficientReason is only populated by the UI when result is UNDETERMINED.
      // ITL-2870: CUSTOM is a scalar-only sentinel ("Custom — see note") — keep the scalar
      // (passed through below), exclude it from the array. proofsSlice mirrors this. See ITL-2922.
      const insufficientReasons =
        formValues?.result === 'UNDETERMINED' &&
        formValues?.resultInsufficientReason &&
        formValues.resultInsufficientReason !== 'NONE' &&
        formValues.resultInsufficientReason !== 'CUSTOM'
          ? ([formValues.resultInsufficientReason] as IProof['result_insufficient_reasons'])
          : null;

      try {
        await dispatch(
          updateProofThunk({
            id: proofId,
            result_insufficient_reason: formValues?.resultInsufficientReason,
            result_insufficient_reasons: insufficientReasons,
            result_edited_reason: formValues?.resultEditedReason,
            note,
            ...result,
            ...type
          })
        );

        showSnackbar(VariantType.success, 'The proof has been updated', SnackTypes.none);
      } catch (error) {
        showSnackbar(VariantType.error, 'Failed to update the proof', SnackTypes.none);
      }
    },
    [SnackTypes.none, VariantType.error, VariantType.success, dispatch, showSnackbar]
  );

  return {
    onUpdateProof
  };
};
